import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';
import { landingPagesApi } from '@/api/landingPages';
import { Badge, Button, EmptyState, Loading } from '@/components/ui';
import type { LandingPage, LandingPageStatus, LandingPageType } from '@/types';

const TYPE_LABELS: Record<LandingPageType, string> = {
  waitlist: 'Waitlist',
  limited: 'Limited',
};

const STATUS_BADGE: Record<LandingPageStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  paused: 'warning',
  closed: 'neutral',
};

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function TypeBadge({ type }: { type: LandingPageType }) {
  return (
    <Badge color={type === 'waitlist' ? '#f59e0b' : '#6366f1'}>
      {TYPE_LABELS[type]}
    </Badge>
  );
}

function StatusBadge({ status }: { status: LandingPageStatus }) {
  return <Badge variant={STATUS_BADGE[status] ?? 'neutral'}>{status}</Badge>;
}

function LandingPageRow({ page, onOpen }: { page: LandingPage; onOpen: (id: string) => void }) {
  return (
    <tr
      className="cursor-pointer transition-colors hover:bg-navy-hover"
      onClick={() => onOpen(page.id)}
    >
      <td className="px-5 py-3.5">
        <span className="font-mono text-xs text-indigo-300">{page.slug}</span>
      </td>
      <td className="px-5 py-3.5">
        <span className="font-medium text-ink-primary">{page.title}</span>
      </td>
      <td className="px-5 py-3.5">
        <TypeBadge type={page.type} />
      </td>
      <td className="px-5 py-3.5">
        <StatusBadge status={page.status} />
      </td>
      <td className="px-5 py-3.5 text-sm text-ink-secondary num">
        {page.signups_count ?? 0}
      </td>
      <td className="px-5 py-3.5 text-xs text-ink-muted">
        {formatDate(page.created_at)}
      </td>
    </tr>
  );
}

export default function LandingPagesListPage() {
  const navigate = useNavigate();

  const { data: pages = [], isLoading } = useQuery({
    queryKey: ['landing-pages'],
    queryFn: () => landingPagesApi.list(),
    staleTime: 30_000,
  });

  return (
    <div dir="ltr">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">Landing Pages</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            {pages.length} landing {pages.length === 1 ? 'page' : 'pages'}
          </p>
        </div>
        <Button size="sm" onClick={() => navigate('/landing-pages/new')}>
          <Plus className="h-4 w-4" />
          New
        </Button>
      </div>

      {isLoading ? (
        <Loading text="Loading landing pages..." />
      ) : pages.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-10 w-10" />}
          title="No landing pages yet. Create your first one."
          actionLabel="New"
          onAction={() => navigate('/landing-pages/new')}
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-border bg-navy/30">
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Slug</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Title</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Type</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Signups</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-ink-muted">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-border">
              {pages.map((page) => (
                <LandingPageRow
                  key={page.id}
                  page={page}
                  onOpen={(id) => navigate(`/landing-pages/${id}`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
